import {
  ColumnDefinition,
  type Options,
  OuiBadgeComponent,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCenterComponent,
  OuiServerSideDatasource as OuiDatasource,
  OuiDataTableComponent,
  OuiDividerComponent,
  OuiGridComponent,
  OuiPaginatorComponent,
  OuiRowComponent,
  OuiStackComponent,
  OuiSymbolComponent,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  type TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import currenciesData from '../../../assets/currencies.json';
import { DownloadButtonComponent } from '../../components/download-button/download-button.component';
import { InputComponent } from '../../components/input/input.component';
import { MultiSelectDropdownComponent } from '../../components/multi-select-dropdown/multi-select-dropdown';
import { PageComponent } from '../../components/page/page';
import {
  OrderHistorySummary,
  type OrderStatusKey,
} from '../../models/order.models';
import { WarehouseOption } from '../../models/warehouse.models';
import { DeviceCareCenterService } from '../../services/device-care-center/device-care-center.service';
import { OrderStatuses } from '../../services/order/order.constants';
import { OrderService } from '../../services/order/order.service';
import { WarehouseManagementSystemService } from '../../services/warehouse-management-system/warehouse-management-system.service';
import { WarehouseService } from '../../services/warehouse/warehouse.service';
import { BasePageComponent } from '../base/base.component';

type CurrencyConfig = {
  code?: string;
  symbol?: string;
  decimal_digits?: number;
};

const currencyEntries = ((currenciesData as { currencies?: CurrencyConfig[] })?.currencies || [])
  .map((currency) => ({
    code: (currency.code || '').trim().toUpperCase(),
    symbol: currency.symbol || '',
    decimals:
      typeof currency.decimal_digits === 'number' ? currency.decimal_digits : 2,
  }))
  .filter((currency) => currency.code);

const currencySymbols = Object.fromEntries(
  currencyEntries.map((currency) => [currency.code, currency.symbol || currency.code]),
);

const currencyDecimals = Object.fromEntries(
  currencyEntries.map((currency) => [currency.code, currency.decimals]),
);

@Component({
  selector: 'app-orders',
  standalone: true,
  imports: [
    OuiBoxComponent,
    OuiStackComponent,
    OuiCenterComponent,
    OuiGridComponent,
    OuiTextComponent,
    OuiSymbolComponent,
    OuiButtonComponent,
    InputComponent,
    OuiDataTableComponent,
    OuiRowComponent,
    OuiPaginatorComponent,
    DownloadButtonComponent,
    OuiDividerComponent,
    PageComponent,
    OuiBadgeComponent,
    MultiSelectDropdownComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.css'],
})
export class OrdersPage extends BasePageComponent {
  private readonly ALL_OPTION_VALUE = '__all__';
  private lastResetRegionId?: number;
  private readonly EXCEPTION_STATUS = 'Exception';

  orders = inject(OrderService);
  dcc = inject(DeviceCareCenterService);
  wms = inject(WarehouseManagementSystemService);
  warehouse = inject(WarehouseService);
  private readonly destroyRef = inject(DestroyRef);
  activeRegionId = this.regions.selectedRegionId;

  orderHistoryColumns!: ColumnDefinition<OrderHistorySummary>[];

  orderDateCell = viewChild<TemplateRef<never>>('orderDateCell');
  orderStatusCell = viewChild<TemplateRef<never>>('orderStatusCell');

  searchForm = new FormGroup({
    buyerCompanyName: new FormControl(''),
    orderNumber: new FormControl(''),
    erpOrderNumber: new FormControl(''),
    startDate: new FormControl(''),
    endDate: new FormControl(''),
    wms: new FormControl<string[]>([this.ALL_OPTION_VALUE]),
    dcc: new FormControl<string[]>([this.ALL_OPTION_VALUE]),
    warehouse: new FormControl<string[]>([this.ALL_OPTION_VALUE]),
    status: new FormControl<string[]>([this.ALL_OPTION_VALUE]),
  });

  selectedDcc = toSignal(this.searchForm.controls.dcc.valueChanges);

  selectedWms = toSignal(this.searchForm.controls.wms.valueChanges);

  wmsOptions = this.wms.warehouseManagementSystemOptions;
  dccOptions = this.dcc.deviceCareCenterOptions;

  warehouseOptions = computed(() => {
    const dccCodes: string[] = this.selectedDcc() ?? [];
    const wmsCodes: string[] = this.selectedWms() ?? [];

    let filter: (option: WarehouseOption) => boolean;

    if (wmsCodes.length && dccCodes.length) {
      filter = (option) =>
        dccCodes.includes(option.dccCode) && wmsCodes.includes(option.wmsCode);
    } else if (wmsCodes.length && !dccCodes.length) {
      filter = (option) => wmsCodes.includes(option.wmsCode);
    } else if (!wmsCodes.length && dccCodes.length) {
      filter = (option) => dccCodes.includes(option.dccCode);
    } else {
      filter = () => true;
    }

    const options = this.warehouse.warehouseOptions().filter(filter);

    return options.length === 0 ? [] : [...options];
  });

  statusOptions: Options = Object.entries(OrderStatuses).map(
    ([key, value]) => ({
      label: value.label,
      value: key,
    }),
  );

  readonly statusTiles = [
    { key: 'ReadyForFulfillment', label: 'Ready For Fulfillment', values: ['ReadyForFulfillment'] },
    { key: 'InProgress', label: 'In Progress', values: ['InProgress'] },
    { key: 'AwaitingExchange', label: 'Awaiting Exchange', values: ['AwaitingExchange'] },
    { key: 'Cancelled', label: 'Cancelled', values: ['Cancelled'] },
  ];

  readonly activeStatusTile = signal<string | null>(null);
  readonly statusTileCounts = signal<Record<string, number>>({});
  readonly isStatusTileCountsLoading = signal(false);
  private statusTileCountsRequestId = 0;

  orderHistoryDatasource = new OuiDatasource<OrderHistorySummary>(
    this.orders.orderDataLoader,
  );

  constructor() {
    super();

    effect(() => {
      const regionId = this.activeRegionId();
      if (regionId && this.lastResetRegionId !== regionId) {
        this.lastResetRegionId = regionId;
        this.resetSearchForm(regionId);
      }
    });

    effect(() => {
      this.applyDefaultAllSelection(
        this.searchForm.controls.wms,
        this.wmsOptions(),
      );
    });

    effect(() => {
      this.applyDefaultAllSelection(
        this.searchForm.controls.dcc,
        this.dccOptions(),
      );
    });

    effect(() => {
      this.applyDefaultAllSelection(
        this.searchForm.controls.warehouse,
        this.warehouseOptions(),
      );
    });

    this.applyDefaultAllSelection(this.searchForm.controls.status, this.statusOptions);

    effect(() => {
      this.orderHistoryColumns = [
        {
          columnDef: 'webOrderId',
          header: 'Order Number',
          isSticky: true,
          textAlign: 'left',
        },
        {
          columnDef: 'shipmentId',
          header: 'Shipment Number',
        },
        {
          columnDef: 'erpOrderNumber',
          header: 'WMS Order Number',
        },
        {
          columnDef: 'orderDate',
          header: 'Order Date',
          template: this.orderDateCell(),
        },
        {
          columnDef: 'dccCode',
          header: 'DCC',
        },
        {
          columnDef: 'warehouseCode',
          header: 'Warehouse',
        },
        {
          columnDef: 'buyerCompanyName',
          header: 'Company Name',
        },
        {
          columnDef: 'status',
          header: 'Status',
          template: this.orderStatusCell(),
        },
        {
          columnDef: 'totalQty',
          header: 'Total Quantity',
        },
        {
          columnDef: 'orderTotal',
          header: 'Order Total',
          cell: (element) =>
            this.formatOrderTotalAmount(element.orderTotal, element.currencyCode),
        },
      ];
    });

    this.orderHistoryDatasource.sortRecords({
      column: 'orderDate',
      direction: 'desc',
    });

    this.searchForm.controls.buyerCompanyName.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.searchForm.controls.buyerCompanyName.dirty) {
          this.searchOrders();
        }
      });
  }

  resetSearchForm(regionId?: number) {
    this.searchForm.reset({
      buyerCompanyName: '',
      orderNumber: '',
      erpOrderNumber: '',
      startDate: '',
      endDate: '',
      wms: [this.ALL_OPTION_VALUE],
      dcc: [this.ALL_OPTION_VALUE],
      warehouse: [this.ALL_OPTION_VALUE],
      status: [this.ALL_OPTION_VALUE],
    });

    this.applyDefaultAllSelection(this.searchForm.controls.status, this.statusOptions);
    this.activeStatusTile.set(null);
    this.orderHistoryDatasource.setFilter({ regionId });
    void this.refreshStatusTileCounts(this.getNormalizedOrderFilters());
  }

  searchFormValue = toSignal(this.searchForm.valueChanges);

  endDateMinValue = computed(() => {
    const valueChanges = this.searchFormValue();
    const startDate = valueChanges?.startDate;
    return typeof startDate === 'string' && startDate.length > 0
      ? startDate
      : undefined;
  });

  searchOrders() {
    const selectedStatuses = this.searchForm.controls.status.value || [];
    this.activeStatusTile.set(this.resolveActiveStatusTile(selectedStatuses));

    const filters = this.getNormalizedOrderFilters();
    this.orderHistoryDatasource.setFilter(this.getCombinedOrderFilters(filters));
    void this.refreshStatusTileCounts(filters);
  }

  private getCombinedOrderFilters(
    baseFilters: ReturnType<typeof this.getNormalizedOrderFilters>,
  ) {
    const activeTileKey = this.activeStatusTile();
    if (!activeTileKey) {
      return baseFilters;
    }

    const tile = this.statusTiles.find((current) => current.key === activeTileKey);
    if (!tile) {
      return baseFilters;
    }

    return {
      ...baseFilters,
      status: [...tile.values],
    };
  }

  private getNormalizedOrderFilters() {
    const { dcc, wms, warehouse, status, ...filters } = this.searchForm.value;

    const normalizedDcc = this.normalizeMultiSelectFilter(
      dcc,
      this.dccOptions(),
    );
    const normalizedWms = this.normalizeMultiSelectFilter(
      wms,
      this.wmsOptions(),
    );
    const normalizedWarehouse = this.normalizeMultiSelectFilter(
      warehouse,
      this.warehouseOptions(),
    );
    const normalizedStatus = this.normalizeMultiSelectFilter(
      status,
      this.statusOptions,
    );

    return {
      ...filters,
      dcc: normalizedDcc,
      wms: normalizedWms,
      warehouse: normalizedWarehouse,
      status: normalizedStatus,
      regionId: this.activeRegionId(),
    };
  }

  private async refreshStatusTileCounts(filters: ReturnType<typeof this.getNormalizedOrderFilters>) {
    const regionId = filters.regionId;

    if (!regionId) {
      this.statusTileCounts.set({});
      return;
    }

    const requestId = ++this.statusTileCountsRequestId;
    this.isStatusTileCountsLoading.set(true);

    try {
      const counts = await this.orders.getStatusCounts({
        regionId,
        startDate: filters.startDate || '',
        endDate: filters.endDate || '',
        buyerCompanyName: filters.buyerCompanyName || '',
        orderNo: filters.orderNumber || undefined,
        dcc: filters.dcc,
        warehouse: filters.warehouse,
        erp: filters.wms,
        status: filters.status,
        salesChannelCode: '',
        erpOrderNumber: filters.erpOrderNumber || undefined,
      });

      if (requestId !== this.statusTileCountsRequestId) return;

      const statusCountMap = Object.fromEntries(
        counts.map(({ status, count }) => [status, count]),
      );

      const tileCounts = Object.fromEntries(
        this.statusTiles.map((tile) => [
          tile.key,
          tile.values.reduce((sum, status) => sum + (statusCountMap[status] ?? 0), 0),
        ]),
      );

      const selectedTileKeys = this.resolveSelectedStatusTileKeys(
        this.searchForm.controls.status.value || [],
      );
      const hasSelectedStatusFilter = Array.isArray(filters.status) && filters.status.length > 0;

      if (selectedTileKeys.length > 0) {
        const selectedSet = new Set(selectedTileKeys);
        const normalizedTileCounts = Object.fromEntries(
          this.statusTiles.map((tile) => [
            tile.key,
            selectedSet.has(tile.key)
              ? Number(tileCounts[tile.key] ?? 0)
              : 0,
          ]),
        );

        this.statusTileCounts.set(normalizedTileCounts);
        return;
      }

      if (hasSelectedStatusFilter) {
        this.statusTileCounts.set(
          Object.fromEntries(this.statusTiles.map((tile) => [tile.key, 0])),
        );
        return;
      }

      this.statusTileCounts.set(tileCounts);
    } finally {
      if (requestId === this.statusTileCountsRequestId) {
        this.isStatusTileCountsLoading.set(false);
      }
    }
  }

  private normalizeMultiSelectFilter(
    selectedValues: string[] | null | undefined,
    options: Array<{ value: unknown }> | undefined,
  ): string[] | undefined {
    const selected = (selectedValues || []).filter(
      (value) => value !== this.ALL_OPTION_VALUE,
    );

    if (selected.length === 0) {
      return undefined;
    }

    const optionValues = (options || []).map((option) => String(option.value));

    // Selecting every option is logically equivalent to no filter.
    if (
      optionValues.length > 0 &&
      selected.length === optionValues.length &&
      optionValues.every((value) => selected.includes(value))
    ) {
      return undefined;
    }

    return selected;
  }

  private applyDefaultAllSelection(
    control: FormControl<string[] | null>,
    options: Array<{ value: unknown }> | undefined,
  ) {
    if (control.dirty) {
      return;
    }

    const optionValues = (options || []).map((option) => String(option.value));
    if (optionValues.length === 0) {
      return;
    }

    const current = control.value || [];
    const hasOnlyAll =
      current.length === 1 && current[0] === this.ALL_OPTION_VALUE;

    if (current.length === 0 || hasOnlyAll) {
      control.setValue([this.ALL_OPTION_VALUE, ...optionValues]);
    }
  }

  handleRowClick(order: OrderHistorySummary) {
    this.router.navigate([`/orders/${order.orderId}`]);
  }

  getDateTime(date: Date): string {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }

  getTodaysDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  ordersDownloadFilename = this.buildOrdersDownloadFilename();

  refreshOrdersDownloadFilename() {
    this.ordersDownloadFilename = this.buildOrdersDownloadFilename();
  }

  private buildOrdersDownloadFilename(): string {
    const now = new Date();
    const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
    return `ATB_ORDERS_${timestamp}`;
  }

  applyStatusTileFilter(tileKey: string) {
    const tile = this.statusTiles.find((current) => current.key === tileKey);
    if (!tile || this.isStatusTileDisabled(tileKey)) {
      return;
    }

    this.searchForm.controls.status.setValue([this.ALL_OPTION_VALUE]);
    this.activeStatusTile.set(tile.key);

    const filters = this.getNormalizedOrderFilters();
    this.orderHistoryDatasource.setFilter(this.getCombinedOrderFilters(filters));
    void this.refreshStatusTileCounts(filters);
  }

  isStatusTileDisabled(tileKey: string): boolean {
    return (this.statusTileCounts()[tileKey] ?? 0) === 0;
  }

  protected getStatusTileCount(tileKey: string) {
    const count = this.statusTileCounts()[tileKey];
    if (typeof count === 'number') return count;
    return this.isStatusTileCountsLoading() ? '...' : 0;
  }

  protected getStatusTileIcon(tileKey: string) {
    switch (tileKey) {
      case 'ReadyForFulfillment':
        return 'task_alt';
      case 'InProgress':
        return 'history';
      case 'AwaitingExchange':
        return 'schedule';
      case 'Cancelled':
        return 'close';
      default:
        return 'schedule';
    }
  }

  private resolveActiveStatusTile(selectedStatuses: string[]): string | null {
    const selected = selectedStatuses.filter(
      (status) => status !== this.ALL_OPTION_VALUE,
    );

    if (selected.length === 0) {
      return null;
    }

    const selectedSet = new Set(selected);
    const matchedTile = this.statusTiles.find((tile) => {
      const tileSet = new Set(tile.values);
      if (tileSet.size !== selectedSet.size) {
        return false;
      }
      return [...tileSet].every((value) => selectedSet.has(value));
    });

    return matchedTile?.key ?? null;
  }

  private resolveSelectedStatusTileKeys(selectedStatuses: string[]): string[] {
    const selected = selectedStatuses.filter(
      (status) => status !== this.ALL_OPTION_VALUE,
    );

    if (selected.length === 0) {
      return [];
    }

    const selectedSet = new Set(selected);
    return this.statusTiles
      .filter((tile) => tile.values.some((value) => selectedSet.has(value)))
      .map((tile) => tile.key);
  }

  protected getStatusLabel(status: string) {
    if (status === this.EXCEPTION_STATUS) {
      return OrderStatuses.Cancelled.label;
    }

    return OrderStatuses[status as OrderStatusKey]?.label || status;
  }

  protected getStatusColor(status: string) {
    if (status === this.EXCEPTION_STATUS) {
      return OrderStatuses.Cancelled.color;
    }

    return OrderStatuses[status as OrderStatusKey]?.color;
  }

  protected formatOrderTotalAmount(amount: number, currencyCode?: string) {
    const code = (currencyCode || '').toUpperCase();
    const symbol = currencySymbols[code] || code || '$';
    const decimals = currencyDecimals[code] ?? 2;

    const formattedValue = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount || 0);

    return `${symbol}${formattedValue}`;
  }
}